const section4MobileMedia = window.matchMedia("(max-width: 767px)");
const section4CarouselStates = new WeakMap();
const mobileCloneAttribute = "data-bl002-mobile-clone";
const autoScrollResumeDelay = 500;

function removeElementIds(element) {
  element.removeAttribute("id");
  element.querySelectorAll("[id]").forEach((child) => child.removeAttribute("id"));
}

function getOriginalSlides(track) {
  return Array.from(track.children).filter(
    (slide) =>
      slide.hasAttribute("data-embla-slide") &&
      !slide.hasAttribute(mobileCloneAttribute),
  );
}

function removeMobileClones(state) {
  state.track
    .querySelectorAll(`[${mobileCloneAttribute}]`)
    .forEach((clone) => clone.remove());
}

function clearAutoScrollResume(state) {
  if (state.autoScrollResumeTimer) {
    window.clearTimeout(state.autoScrollResumeTimer);
    state.autoScrollResumeTimer = 0;
  }
}

function canUseMobileAutoScroll(state) {
  return (
    state.mode === "mobile" &&
    state.root.dataset.emblaDisableDrag !== "true" &&
    state.root.dataset.emblaAutoscroll === "true"
  );
}

function pauseMobileAutoScroll(state) {
  if (!canUseMobileAutoScroll(state)) return;

  clearAutoScrollResume(state);
  state.api.plugins().autoScroll?.stop();
  state.autoScrollPaused = true;
}

function resumeMobileAutoScroll(state) {
  if (!canUseMobileAutoScroll(state)) return;

  clearAutoScrollResume(state);
  state.api.plugins().autoScroll?.play();
  state.autoScrollPaused = false;
}

function scheduleMobileAutoScrollResume(state) {
  if (!canUseMobileAutoScroll(state)) return;

  clearAutoScrollResume(state);
  state.autoScrollResumeTimer = window.setTimeout(() => {
    state.autoScrollResumeTimer = 0;
    if (!state.isPointerDown) resumeMobileAutoScroll(state);
  }, autoScrollResumeDelay);
}

function bindAutoScrollInteraction(state) {
  const onPointerDown = () => {
    state.isPointerDown = true;
    pauseMobileAutoScroll(state);
  };
  const onPointerUp = () => {
    state.isPointerDown = false;
    scheduleMobileAutoScrollResume(state);
  };
  const onSettle = () => {
    if (state.autoScrollPaused && !state.isPointerDown) {
      resumeMobileAutoScroll(state);
    }
  };
  const onAutoScrollInteraction = (_api, event) => {
    const interaction = event?.detail?.interaction;
    if (interaction === "pointerdown") onPointerDown();
    if (interaction === "pointerup") onPointerUp();
  };

  state.onPointerDown = onPointerDown;
  state.onPointerUp = onPointerUp;
  state.onSettle = onSettle;
  state.onAutoScrollInteraction = onAutoScrollInteraction;
  state.api.on("pointerDown", onPointerDown);
  state.api.on("pointerUp", onPointerUp);
  state.api.on("settle", onSettle);
  state.api.on("autoscroll:interaction", onAutoScrollInteraction);
}

function unbindAutoScrollInteraction(state) {
  state.api.off("pointerDown", state.onPointerDown);
  state.api.off("pointerUp", state.onPointerUp);
  state.api.off("settle", state.onSettle);
  state.api.off("autoscroll:interaction", state.onAutoScrollInteraction);
  clearAutoScrollResume(state);
}

function updateMobileMarquee(state) {
  if (state.mode !== "mobile") return;

  window.cancelAnimationFrame(state.resizeFrame);
  state.resizeFrame = window.requestAnimationFrame(() => {
    state.resizeFrame = 0;
    if (state.mode !== "mobile") return;

    const originals = getOriginalSlides(state.track);
    const firstClone = state.track.querySelector(`[${mobileCloneAttribute}]`);
    if (!originals.length || !firstClone) return;

    const distance =
      firstClone.getBoundingClientRect().left -
      originals[0].getBoundingClientRect().left;
    if (distance <= 0) return;
    if (
      state.track.classList.contains("bl002-mobile-marquee") &&
      Math.abs(state.marqueeDistance - distance) < 0.5
    ) {
      return;
    }

    state.marqueeDistance = distance;
    state.track.style.setProperty("--bl002-marquee-distance", `${-distance}px`);
    state.track.style.setProperty(
      "--bl002-marquee-duration",
      `${Math.max(12, distance / 60)}s`,
    );
    // Keep layout and the animation intact during size notifications.
    state.track.classList.add("bl002-mobile-marquee");
  });
}

function enterMobileMode(state) {
  if (state.mode === "mobile") {
    updateMobileMarquee(state);
    return;
  }

  state.mode = "mobile";
  const dragEnabled = state.root.dataset.emblaDisableDrag !== "true";

  if (dragEnabled) {
    window.cancelAnimationFrame(state.resizeFrame);
    state.resizeFrame = 0;
    clearAutoScrollResume(state);
    state.track.classList.remove("bl002-mobile-marquee");
    state.track.style.removeProperty("--bl002-marquee-distance");
    state.track.style.removeProperty("--bl002-marquee-duration");
    state.marqueeDistance = 0;
    removeMobileClones(state);
    // Keep Embla active when mobile dragging is enabled in the block settings.
    state.api.reInit({ active: true, draggable: true }, state.desktopPlugins);
    if (state.root.dataset.emblaAutoscroll === "true") {
      state.api.plugins().autoScroll?.play();
      state.autoScrollPaused = false;
    }
    return;
  }

  // Stop the engine and its plugins, not just AutoScroll's animation.
  state.api.reInit({ active: false }, []);

  removeMobileClones(state);
  const originals = getOriginalSlides(state.track);
  originals.forEach((slide) => {
    const clone = slide.cloneNode(true);
    removeElementIds(clone);
    clone.setAttribute(mobileCloneAttribute, "true");
    clone.setAttribute("aria-hidden", "true");
    clone.inert = true;
    state.track.append(clone);
  });

  updateMobileMarquee(state);
}

function enterDesktopMode(state) {
  if (state.mode === "desktop") return;

  state.mode = "desktop";
  window.cancelAnimationFrame(state.resizeFrame);
  state.resizeFrame = 0;
  clearAutoScrollResume(state);
  state.isPointerDown = false;
  state.autoScrollPaused = false;
  state.track.classList.remove("bl002-mobile-marquee");
  state.track.style.removeProperty("--bl002-marquee-distance");
  state.track.style.removeProperty("--bl002-marquee-duration");
  state.marqueeDistance = 0;
  removeMobileClones(state);

  state.api.reInit(state.desktopOptions, state.desktopPlugins);
  if (state.root.dataset.emblaAutoscroll === "true") {
    state.api.plugins().autoScroll?.play();
  }
  if (state.root.dataset.emblaAutoplay === "true") {
    state.api.plugins().autoplay?.play();
  }
}

function syncCarouselMode(state) {
  if (section4MobileMedia.matches) {
    enterMobileMode(state);
  } else if (state.mode === null) {
    state.mode = "desktop";
  } else {
    enterDesktopMode(state);
  }
}

function bindSection4Carousel(root) {
  const api = root.__tideweEmblaApi;
  const viewport = root.querySelector("[data-embla-viewport]");
  const track = viewport?.querySelector("[data-embla-container]");
  if (!api || !viewport || !track) return false;

  const currentState = section4CarouselStates.get(root);
  if (currentState?.api === api) return true;
  currentState?.cleanup();

  const state = {
    api,
    desktopOptions: { ...api.internalEngine().options },
    desktopPlugins: Object.values(api.plugins()),
    marqueeDistance: 0,
    mode: null,
    resizeFrame: 0,
    isPointerDown: false,
    autoScrollPaused: false,
    autoScrollResumeTimer: 0,
    root,
    track,
  };

  const sync = () => syncCarouselMode(state);
  let viewportWidth = null;
  const resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;
    const width = entry.contentRect.width;
    // Height changes (including mobile browser chrome) must not restart a loop.
    if (width === viewportWidth) return;
    viewportWidth = width;
    updateMobileMarquee(state);
  });
  const cleanup = () => {
    window.cancelAnimationFrame(state.resizeFrame);
    unbindAutoScrollInteraction(state);
    section4MobileMedia.removeEventListener("change", sync);
    resizeObserver.disconnect();
    state.track.classList.remove("bl002-mobile-marquee");
    state.track.style.removeProperty("--bl002-marquee-distance");
    state.track.style.removeProperty("--bl002-marquee-duration");
    removeMobileClones(state);
    if (section4CarouselStates.get(root) === state) {
      section4CarouselStates.delete(root);
    }
  };

  state.cleanup = cleanup;
  section4CarouselStates.set(root, state);
  bindAutoScrollInteraction(state);
  section4MobileMedia.addEventListener("change", sync);
  resizeObserver.observe(viewport);
  api.on("destroy", cleanup);
  sync();

  return true;
}

function initializeSection4Carousels(root = document) {
  const sections = root.matches?.('[handle="section4"]')
    ? [root]
    : root.querySelectorAll?.('[handle="section4"]') || [];

  sections.forEach((section) => {
    section
      .querySelectorAll('[data-embla][handle="embla"]')
      .forEach(bindSection4Carousel);
  });
}

const initializationObserver = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    const root = mutation.target;
    if (
      root instanceof HTMLElement &&
      root.matches('[data-embla][handle="embla"]') &&
      root.closest('[handle="section4"]')
    ) {
      bindSection4Carousel(root);
    }
  });
});

function initializePage() {
  initializeSection4Carousels();
  initializationObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-embla-initialized"],
    subtree: true,
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializePage, { once: true });
} else {
  initializePage();
}

document.addEventListener("shopify:section:load", (event) => {
  initializeSection4Carousels(event.target);
});
